import { useEffect, useMemo, useRef, useState } from 'react'

import type { Placement, TourStep } from '@ui/components/Tourtip/Tourtip'

type Pos = { top: number; left: number }

const MOBILE_WIDTH_THRESHOLD = 480
const EDGE_PADDING_MOBILE = 16
const EDGE_PADDING_DESKTOP = 24
const DESIRED_ARROW_TARGET_SPACE = 4
const POINTER_SIZE = 12
const POINTER_PROTRUSION = POINTER_SIZE
const GAP = DESIRED_ARROW_TARGET_SPACE + POINTER_PROTRUSION

export interface UseTourtipPositionResult {
  boxRef: React.RefObject<HTMLDivElement | null>
  style: React.CSSProperties | undefined
  pointerPlacement: Placement | null
  targetRect: DOMRect | null
}

function resolvePreferredPlacement(
  pref: TourStep['preferredPlacement'],
  isMobile: boolean
): Placement | null {
  if (!pref) return null
  if (typeof pref === 'string') return pref
  return isMobile ? pref.mobile : pref.desktop
}

export function useTourtipPosition(step?: TourStep): UseTourtipPositionResult {
  const boxRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<Pos | null>(null)
  const [placement, setPlacement] = useState<Placement | null>(null)
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)
  const scrolledForStepRef = useRef<string | null>(null)

  const targetEl = useMemo<HTMLElement | null>(() => {
    if (!step || step.kind !== 'regular') return null
    if (step.target.kind !== 'data-attr') return null
    return document.querySelector(`[data-tour-target="${step.target.key}"]`) as HTMLElement | null
  }, [
    step?.id,
    step?.kind,
    step && step.kind === 'regular' && step.target.kind === 'data-attr' ? step.target.key : null,
  ])

  useEffect(() => {
    if (!step || step.kind !== 'regular' || !targetEl) {
      setPos(null)
      setPlacement(null)
      setTargetRect(null)
      scrolledForStepRef.current = null
      return
    }

    const tipEl = boxRef.current
    if (!tipEl) return

    const compute = () => {
      const rect = targetEl.getBoundingClientRect()
      setTargetRect(rect)
      const { finalTop, finalLeft, bestPlacement } = calculateBestPosition(tipEl, rect, step)
      setPos({ top: Math.round(finalTop), left: Math.round(finalLeft) })
      setPlacement(bestPlacement)
    }

    const maybeScrollIntoView = () => {
      const vw = window.innerWidth
      const vh = window.innerHeight
      const isMobile = vw <= MOBILE_WIDTH_THRESHOLD
      const edgePadding = isMobile ? EDGE_PADDING_MOBILE : EDGE_PADDING_DESKTOP

      const rect = targetEl.getBoundingClientRect()
      const outOfView =
        rect.top < edgePadding ||
        rect.bottom > vh - edgePadding ||
        rect.left < edgePadding ||
        rect.right > vw - edgePadding

      if (outOfView && scrolledForStepRef.current !== step.id) {
        scrolledForStepRef.current = step.id
        smoothScrollTargetIntoCenter(targetEl)
      }
    }

    const animationFrameId = requestAnimationFrame(() => {
      maybeScrollIntoView()
      compute()
    })
    const onWin = () => compute()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, { passive: true })

    const ro = new ResizeObserver(() => compute())
    ro.observe(targetEl)

    const roTip = new ResizeObserver(() => compute())
    if (tipEl) roTip.observe(tipEl)

    return () => {
      cancelAnimationFrame(animationFrameId)
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin)
      ro.disconnect()
      roTip.disconnect()
    }
  }, [step, targetEl])

  const style: React.CSSProperties | undefined = useMemo(() => {
    if (!step || step.kind !== 'regular') return undefined
    if (!pos) return { visibility: 'hidden' }
    return {
      position: 'fixed',
      top: `${pos.top}px`,
      left: `${pos.left}px`,
      transform: 'none',
      visibility: 'visible',
    }
  }, [pos, step])

  return { boxRef, style, pointerPlacement: placement, targetRect }
}

function getComputedStyleSafe(el: Element) {
  return window.getComputedStyle(el)
}

function isScrollable(el: Element): boolean {
  const style = getComputedStyleSafe(el)
  const overflowY = style.overflowY
  const overflowX = style.overflowX
  const canScrollY =
    (overflowY === 'auto' || overflowY === 'scroll') &&
    (el as HTMLElement).scrollHeight > (el as HTMLElement).clientHeight
  const canScrollX =
    (overflowX === 'auto' || overflowX === 'scroll') &&
    (el as HTMLElement).scrollWidth > (el as HTMLElement).clientWidth
  return canScrollY || canScrollX
}

function getScrollParent(el: Element): Element | Document | null {
  let parent: Element | null = el.parentElement
  while (parent) {
    if (isScrollable(parent)) return parent
    parent = parent.parentElement
  }
  return document.scrollingElement ? document.scrollingElement : document.documentElement
}

function smoothScrollTargetIntoCenter(targetEl: HTMLElement) {
  const container = getScrollParent(targetEl)
  const targetRect = targetEl.getBoundingClientRect()

  if (
    container &&
    container instanceof Element &&
    container !== document.documentElement &&
    container !== document.body
  ) {
    const cRect = container.getBoundingClientRect()
    const deltaY = targetRect.top - cRect.top - (cRect.height - targetRect.height) / 2
    const deltaX = targetRect.left - cRect.left - (cRect.width - targetRect.width) / 2
    const nextTop = (container as HTMLElement).scrollTop + deltaY
    const nextLeft = (container as HTMLElement).scrollLeft + deltaX
    ;(container as HTMLElement).scrollTo({ top: nextTop, left: nextLeft, behavior: 'smooth' })
  } else {
    const vw = window.innerWidth
    const vh = window.innerHeight
    const absTop = window.scrollY + targetRect.top
    const absLeft = window.scrollX + targetRect.left
    const top = Math.max(0, absTop - (vh - targetRect.height) / 2)
    const left = Math.max(0, absLeft - (vw - targetRect.width) / 2)
    window.scrollTo({ top, left, behavior: 'smooth' })
  }
}

function prepareElementForMeasurement(el: HTMLElement) {
  const prev = {
    visibility: el.style.visibility,
    transform: el.style.transform,
    left: el.style.left,
    top: el.style.top,
    position: el.style.position,
  }
  el.style.visibility = 'hidden'
  el.style.transform = 'none'
  el.style.left = '0px'
  el.style.top = '0px'
  el.style.position = 'fixed'
  return prev
}

function restoreElementStyles(
  el: HTMLElement,
  prev: ReturnType<typeof prepareElementForMeasurement>
) {
  el.style.visibility = prev.visibility
  el.style.transform = prev.transform
  el.style.left = prev.left
  el.style.top = prev.top
  el.style.position = prev.position
}

function getCandidates(rect: DOMRect, step: TourStep): Placement[] {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const isMobile = vw <= MOBILE_WIDTH_THRESHOLD
  const edgePadding = isMobile ? EDGE_PADDING_MOBILE : EDGE_PADDING_DESKTOP

  const spaceTop = rect.top - edgePadding
  const spaceBottom = vh - rect.bottom - edgePadding
  const spaceLeft = rect.left - edgePadding
  const spaceRight = vw - rect.right - edgePadding

  const rawPreferred = resolvePreferredPlacement(step.preferredPlacement, isMobile)
  const isSide = (p: Placement | null) => !!p && (p.startsWith('left') || p.startsWith('right'))
  const preferred: Placement | null =
    isMobile && isSide(rawPreferred)
      ? spaceBottom >= spaceTop
        ? 'top-center'
        : 'bottom-center'
      : rawPreferred

  const sideToPlacements: Record<'top' | 'bottom' | 'left' | 'right', Placement[]> = isMobile
    ? {
        top: ['top-center', 'top-left', 'top-right'],
        bottom: ['bottom-center', 'bottom-left', 'bottom-right'],
        left: [],
        right: [],
      }
    : {
        top: ['top-center', 'top-left', 'top-right'],
        bottom: ['bottom-center', 'bottom-left', 'bottom-right'],
        left: ['left-center', 'left-top', 'left-bottom'],
        right: ['right-center', 'right-top', 'right-bottom'],
      }

  const bySpace = [
    { side: 'bottom' as const, space: spaceBottom },
    { side: 'top' as const, space: spaceTop },
    { side: 'right' as const, space: spaceRight },
    { side: 'left' as const, space: spaceLeft },
  ]
    .filter((s) => sideToPlacements[s.side].length > 0)
    .sort((a, b) => b.space - a.space)

  const order: Placement[] = []
  if (preferred) order.push(preferred)
  bySpace.forEach(({ side }) => {
    order.push(...sideToPlacements[side])
  })
  const used = new Set<string>()
  return order.filter((p) => (used.has(p) ? false : (used.add(p), true)))
}

function getCandidatePosition(
  p: Placement,
  rect: DOMRect,
  tw: number,
  th: number
): { top: number; left: number } {
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2
  const vw = window.innerWidth
  const isMobile = vw <= MOBILE_WIDTH_THRESHOLD

  const horizontalCenter = isMobile ? vw / 2 - tw / 2 : centerX - tw / 2
  const horizontalLeft = isMobile ? vw / 2 - tw / 2 : rect.left
  const horizontalRight = isMobile ? vw / 2 - tw / 2 : rect.right - tw

  const positions: Record<Placement, { top: number; left: number }> = {
    'top-center': { top: rect.bottom + GAP, left: horizontalCenter },
    'top-left': { top: rect.bottom + GAP, left: horizontalLeft },
    'top-right': { top: rect.bottom + GAP, left: horizontalRight },
    'bottom-center': { top: rect.top - GAP - th, left: horizontalCenter },
    'bottom-left': { top: rect.top - GAP - th, left: horizontalLeft },
    'bottom-right': { top: rect.top - GAP - th, left: horizontalRight },
    'left-center': { top: centerY - th / 2, left: isMobile ? vw / 2 - tw / 2 : rect.right + GAP },
    'left-top': { top: rect.top, left: isMobile ? vw / 2 - tw / 2 : rect.right + GAP },
    'left-bottom': { top: rect.bottom - th, left: isMobile ? vw / 2 - tw / 2 : rect.right + GAP },
    'right-center': {
      top: centerY - th / 2,
      left: isMobile ? vw / 2 - tw / 2 : rect.left - GAP - tw,
    },
    'right-top': { top: rect.top, left: isMobile ? vw / 2 - tw / 2 : rect.left - GAP - tw },
    'right-bottom': {
      top: rect.bottom - th,
      left: isMobile ? vw / 2 - tw / 2 : rect.left - GAP - tw,
    },
    auto: { top: window.innerHeight / 2 - th / 2, left: window.innerWidth / 2 - tw / 2 },
  }

  return positions[p] || { top: 0, left: 0 }
}

function calculateBestPosition(tipEl: HTMLElement, rect: DOMRect, step: TourStep) {
  const prev = prepareElementForMeasurement(tipEl)
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const tooltipWidth = tipEl.offsetWidth
  const tooltipHeight = tipEl.offsetHeight
  const isMobile = viewportWidth <= MOBILE_WIDTH_THRESHOLD
  const edgePadding = isMobile ? EDGE_PADDING_MOBILE : EDGE_PADDING_DESKTOP

  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))

  const candidates = getCandidates(rect, step)
  type Cand = { placement: Placement; top: number; left: number; overflow: number }

  const candList: Cand[] = candidates.map((p) => {
    const pos = getCandidatePosition(p, rect, tooltipWidth, tooltipHeight)
    const overflowLeft = Math.max(0, edgePadding - pos.left)
    const overflowTop = Math.max(0, edgePadding - pos.top)
    const overflowRight = Math.max(0, pos.left + tooltipWidth - (viewportWidth - edgePadding))
    const overflowBottom = Math.max(0, pos.top + tooltipHeight - (viewportHeight - edgePadding))
    return {
      placement: p,
      top: pos.top,
      left: pos.left,
      overflow: overflowLeft + overflowTop + overflowRight + overflowBottom,
    }
  })

  const fitting = candList.find((c) => c.overflow === 0)
  const best = fitting ?? candList.sort((a, b) => a.overflow - b.overflow)[0]

  const finalTop = clamp(best.top, edgePadding, viewportHeight - edgePadding - tooltipHeight)
  const finalLeft = clamp(best.left, edgePadding, viewportWidth - edgePadding - tooltipWidth)

  restoreElementStyles(tipEl, prev)

  return { finalTop, finalLeft, bestPlacement: best.placement }
}
